import { useState } from "react";
import { Copy, ExternalLink } from "lucide-react";
import { AcDialog, Button, Field, Icon, Spinner } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import {
  useReleaseShares,
  useMintShare,
  useRevokeShare,
  activeShare,
  type ReleaseShareMintResult,
} from "../../app/hooks/useReleaseShares.js";
import { PUBLIC_WEB_URL } from "../../config/env.js";
import styles from "./ShareLinkBar.module.scss";

// The private SHARE-LINK bar (shot-09) — an Off/Active toggle over POST·GET·DELETE /releases/{id}/shares.
// Every release starts OFF and the band decides when to activate it (ART-218, PO 2026-10-01). Activate mints a
// /r/{token} page; Deactivate revokes it, and activating again mints a NEW url. The live share's path is
// re-served by the GET, so Copy and Open work on every visit. They used to work only in the session that
// minted the link (the token was stored hashed alone), which left an "active" link whose only control was
// revoke. Publish actions are never optimistic: mint/revoke are server-confirm then repaint.
//
// This is NOT the smart link. Step 04's smart link is the PUBLIC fan-facing landing page; this mints a
// revocable PRIVATE page at /r/{token} for distro and press. Two links, two audiences — never merge them.
//
// There is deliberately no Set/Remove password affordance on a live link: POST mints with an optional
// password, GET lists, DELETE revokes, and there is nothing in between (audit G-7). A button that cannot work
// is worse than its absence; the honest path is deactivate + re-activate, which is a NEW url.

export function ShareLinkBar({ releaseId }: { releaseId: string }) {
  const canWrite = useCapability("releases:write");
  const shares = useReleaseShares(releaseId);
  const mint = useMintShare(releaseId);
  const revoke = useRevokeShare(releaseId);
  const [minted, setMinted] = useState<ReleaseShareMintResult | null>(null);
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const active = activeShare(shares.data);
  const url = absoluteShareUrl(minted?.path ?? active?.path);
  const hasLink = Boolean(minted) || Boolean(active);
  const passwordProtected = minted ? minted.hasPassword : active?.hasPassword;

  function create() {
    mint.mutate(
      { password: password.trim() || null },
      {
        onSuccess: (result) => {
          setMinted(result);
          setPassword("");
        },
      },
    );
  }

  function deactivate() {
    const shareId = minted?.shareId ?? active?.shareId;
    if (!shareId) return;
    revoke.mutate(shareId, {
      onSuccess: () => {
        setMinted(null);
        setConfirmDeactivate(false);
      },
    });
  }

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className={styles.bar}>
      <span className={styles.label}>Private link</span>

      {shares.isLoading ? (
        <Spinner size={16} label="Loading link…" />
      ) : shares.isError ? (
        // A failed load is NOT "no link" — surfacing the empty state here would invite minting a SECOND live
        // share the user can't see (revoke only tombstones one row). Show a distinct retry instead.
        <>
          <span className={styles.empty}>Couldn't load the private link.</span>
          <Button variant="ghost" size="sm" onClick={() => void shares.refetch()}>
            Retry
          </Button>
        </>
      ) : hasLink ? (
        <>
          <span className={styles.state} data-state="active">
            Active
          </span>
          <div className={styles.urlField}>
            {url ? (
              <input className={styles.url} readOnly value={url} aria-label="Private link URL" />
            ) : (
              // Only a share minted before the URL was kept can land here, and the migration that added it
              // revoked those. Kept as a truthful fallback rather than a blank field.
              <span className={styles.urlHidden}>URL unavailable — deactivate and activate again for a new one</span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => void copy()} disabled={!url}>
            <Icon icon={Copy} size={16} /> {copied ? "Copied" : "Copy"}
          </Button>
          <Button variant="ghost" size="sm" disabled={!url} onClick={() => url && window.open(url, "_blank", "noopener")}>
            <Icon icon={ExternalLink} size={16} /> Open
          </Button>
          <span className={styles.passwordState}>{passwordProtected ? "Password protected" : "No password"}</span>
          {canWrite && (
            <Button variant="ghost" size="sm" onClick={() => setConfirmDeactivate(true)}>
              Deactivate
            </Button>
          )}
        </>
      ) : (
        <>
          <span className={styles.state} data-state="off">
            Off
          </span>
          <span className={styles.empty}>
            Activate a private page to send this release to distro or press. Add a password for extra control.
          </span>
          {canWrite && (
            <>
              <div className={styles.passwordInput}>
                {/* The caption sits LEFT of the box to keep the bar one row tall. It is presentational: the
                    Field keeps its own label (screen-reader only via hideLabel) as the accessible name, so the
                    two never read twice. */}
                <span className={styles.passwordLabel} aria-hidden="true">
                  Password (optional)
                </span>
                {/* `secureEntry`, not `type="password"` — for a stronger version of the vendor-credential
                    reason (the retired Bandsintown sign-in was the other use). This is not
                    a credential AT ALL: it is a gate the band MINTS on one link, typed once and never read
                    back (there is no set/remove endpoint — see the note at the top of this file). So a
                    manager has nothing right to offer and nothing worth saving, and a "Save password?" prompt
                    would file a per-link gate under this origin as if it were the account's.
                    `type="password"` + `autocomplete="off"` — what this was — is the configuration browsers
                    ignore: Chrome and Safari fill and offer to save a password-typed field regardless, which
                    is the autofill that was reported. `secureEntry` drops the password TYPE (CSS-masked text)
                    and adds the manager-ignore attributes, so there is no field for any of them to act on.
                    The ART-7 ruling this inverts is about ACCOUNT passwords, which must stay real password
                    fields precisely so a manager CAN scope them; this is the opposite case. */}
                <Field
                  label="Password (optional)"
                  hideLabel
                  secureEntry
                  placeholder="Add password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <Button size="sm" onClick={create} loading={mint.isPending}>
                Activate link
              </Button>
            </>
          )}
        </>
      )}

      {/* Deactivating is destructive, immediate and irreversible — everyone holding the URL loses access the
          moment it lands, and activating again mints a different URL. The branded AcDialog (never a hand-rolled
          dialog, never window.confirm). */}
      {confirmDeactivate && (
        <AcDialog
          title="Deactivate private link"
          message="Deactivate this link? Everyone who has it loses access immediately. Activating again creates a new URL."
          confirmLabel="Deactivate"
          tone="danger"
          loading={revoke.isPending}
          onConfirm={deactivate}
          onCancel={() => setConfirmDeactivate(false)}
        />
      )}
    </div>
  );
}

/** Absolute public URL for a share path (`/r/{token}`) — prepends the public web host (config, invariant 4). */
function absoluteShareUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  const base = PUBLIC_WEB_URL || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}${path}`;
}
